import axios from "axios";
import { COMPONENTS_BASE_URL } from "shared/constants/backend";
import { Component, ComponentRole, User, UserOption } from "shared/models";
import { generateAuthorizationHeaders } from "shared/utils";

export async function getComponents(token = ""): Promise<Component[]> {
  const headers = generateAuthorizationHeaders(token);
  const response = await axios.get<Component[]>(`${COMPONENTS_BASE_URL}/`, {
    headers,
  });
  return response.data;
}

export async function createComponent(
  name: string,
  url: string | null,
  token = ""
): Promise<Component> {
  const headers = generateAuthorizationHeaders(token);
  const response = await axios.post<Component>(
    `${COMPONENTS_BASE_URL}/`,
    { name, url },
    { headers }
  );
  return response.data;
}

export async function updateComponent(
  componentId: number,
  payload: { name?: string; url?: string | null },
  token = ""
): Promise<Component> {
  const headers = generateAuthorizationHeaders(token);
  const response = await axios.patch<Component>(
    `${COMPONENTS_BASE_URL}/${componentId}/`,
    payload,
    { headers }
  );
  return response.data;
}

export async function deleteComponent(
  componentId: number,
  token = ""
): Promise<void> {
  const headers = generateAuthorizationHeaders(token);
  await axios.delete(`${COMPONENTS_BASE_URL}/${componentId}/`, { headers });
}

export async function getComponentRoles(
  componentId: number,
  token = ""
): Promise<ComponentRole[]> {
  const headers = generateAuthorizationHeaders(token);
  const response = await axios.get<ComponentRole[]>(
    `${COMPONENTS_BASE_URL}/${componentId}/roles/`,
    { headers }
  );
  return response.data;
}

export async function createComponentRole(
  componentId: number,
  name: string,
  token = ""
): Promise<ComponentRole> {
  const headers = generateAuthorizationHeaders(token);
  const response = await axios.post<ComponentRole>(
    `${COMPONENTS_BASE_URL}/${componentId}/roles/`,
    { name },
    { headers }
  );
  return response.data;
}

export async function updateComponentRolePolicy(
  componentId: number,
  roleId: number,
  groupAdminManageable: boolean,
  token = ""
): Promise<ComponentRole> {
  const headers = generateAuthorizationHeaders(token);
  const response = await axios.patch<ComponentRole>(
    `${COMPONENTS_BASE_URL}/${componentId}/roles/${roleId}/`,
    { groupAdminManageable },
    { headers }
  );
  return response.data;
}

export async function deleteComponentRole(
  componentId: number,
  roleId: number,
  token = ""
): Promise<void> {
  const headers = generateAuthorizationHeaders(token);
  await axios.delete(`${COMPONENTS_BASE_URL}/${componentId}/roles/${roleId}/`, {
    headers,
  });
}

export async function getComponentTechadmins(
  componentId: number,
  token = ""
): Promise<User[]> {
  const headers = generateAuthorizationHeaders(token);
  const response = await axios.get<User[]>(
    `${COMPONENTS_BASE_URL}/${componentId}/techadmins/`,
    { headers }
  );
  return response.data;
}

export async function updateComponentTechadmins(
  componentId: number,
  userIds: string[],
  token = ""
): Promise<User[]> {
  const headers = generateAuthorizationHeaders(token);
  const response = await axios.put<User[]>(
    `${COMPONENTS_BASE_URL}/${componentId}/techadmins/`,
    { userIds },
    { headers }
  );
  return response.data;
}

export async function getComponentEligibleUsers(
  componentId: number,
  token = ""
): Promise<UserOption[]> {
  const headers = generateAuthorizationHeaders(token);
  const response = await axios.get<UserOption[]>(
    `${COMPONENTS_BASE_URL}/${componentId}/eligible-users/`,
    { headers }
  );
  return response.data;
}
